import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium " +
    "transition-[background,border-color,box-shadow,color] duration-100 " +
    "disabled:pointer-events-none disabled:opacity-50 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
  {
    variants: {
      variant: {
        // Primer primary — green like GitHub's "Create" actions
        primary:
          "bg-success-emphasis text-white border border-success-emphasis " +
          "hover:bg-[rgb(46,160,67)] active:bg-[rgb(38,140,58)]",
        // Default — neutral surface, subtle border (Primer "Button")
        default:
          "bg-subtle text-foreground border border-border " +
          "hover:bg-[rgb(var(--of-canvas-inset))] active:bg-[rgb(var(--of-canvas-inset))] " +
          "shadow-of-sm",
        // Outline blue — Primer "accent" variant
        accent:
          "bg-background text-accent border border-accent/40 " +
          "hover:bg-accent-subtle active:bg-accent-subtle/80",
        // Danger
        danger:
          "bg-danger-emphasis text-white border border-danger-emphasis " +
          "hover:bg-danger active:bg-danger-emphasis",
        // Quiet / ghost
        ghost:
          "bg-transparent text-foreground border border-transparent " +
          "hover:bg-subtle active:bg-[rgb(var(--of-canvas-inset))]",
        link: "bg-transparent text-accent underline-offset-2 hover:underline border-0 p-0",
      },
      size: {
        sm: "h-7 px-2 text-xs",
        md: "h-8 px-3 text-sm",
        lg: "h-9 px-4 text-md",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />;
  },
);
Button.displayName = "Button";

export { buttonVariants };
